// src/ClaimForm.js
import React, { useState, useEffect } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { generateBase64UUID } from "./utility/utils";
import {
	TextField,
	MenuItem,
	Container,
	Typography,
	Button,
	IconButton,
	Box,
	Grid2,
	AppBar,
	Toolbar,
	Divider,
	Paper,
	CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
	fetchOpenSignTemplates,
	createAssignment,
	fetchUsers,
	getInsuranceCompanies,
	updateAssignment,
	uploadBulkAssignments,
	uploadAttachment,
	createClaimant,
	updateClaimant,
	deleteClaimant,
} from "./utility/api";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsuredCrudGrid from "./component/InsuredGrid";
import { validateRequiredClaimDates } from "./utility/claim-workflow";

const parseEnvList = (envVar, fallback = []) => {
	const value = envVar;
	if (!value) return fallback;
	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
};

const assignmentTypes = parseEnvList(process.env.REACT_APP_ASSIGNMENT_TYPES, [
	"CAT Loss - Flood",
]);
const natureOfLosses = parseEnvList(process.env.REACT_APP_NATURE_OF_LOSSES, [
	"Flood",
	"Windstorm",
	"Flood And Windstorm",
]);
const branches = parseEnvList(process.env.REACT_APP_BRANCHES, [
	"PG",
	"IP",
	"JB",
	"KK",
	"KCH",
	"KL",
	"KTN",
]);
const policyTypes = parseEnvList(process.env.REACT_APP_POLICY_TYPES, [
	"Householder & Houseowner",
	"Fire",
]);
const assignmentModes = parseEnvList(process.env.REACT_APP_ASSIGNMENT_MODES, [
	"R",
	"P",
]);

// Words stripped before token-matching company names against template names.
// Common corporate suffixes that carry no insurer-identity meaning.
// "malaysia", "general", "am", "single", "multiple" are KEPT — they disambiguate
// insurers like Liberty General vs Liberty, Pacific Single vs Pacific Multiple.
const TEMPLATE_MATCH_STOPWORDS = new Set([
	"insurance",
	"berhad",
	"bhd",
	"sdn",
	"co",
	"company",
	"limited",
	"ltd",
	"the",
	"acceptance",
	"form",
]);

const tokenizeForTemplateMatch = (name) =>
	(name || "")
		.toLowerCase()
		.replace(/[^a-z0-9\s]+/g, " ")
		.split(/\s+/)
		.filter((t) => t && !TEMPLATE_MATCH_STOPWORDS.has(t));

const findMatchingTemplates = (companyName, allTemplates) => {
	if (!companyName || !Array.isArray(allTemplates) || allTemplates.length === 0) {
		return [];
	}
	const companyTokens = tokenizeForTemplateMatch(companyName);
	if (companyTokens.length === 0) return [];
	// Anchor token: the leftmost significant token (e.g. "lonpac", "liberty",
	// "generali", "syarikat"). A template must contain this to be considered.
	const anchorToken = companyTokens[0];
	return allTemplates
		.map((t) => {
			const templateTokens = tokenizeForTemplateMatch(t.name);
			if (!templateTokens.includes(anchorToken)) return null;
			const overlap = companyTokens.filter((tk) =>
				templateTokens.includes(tk),
			).length;
			const endsWithAcceptanceForm = /acceptance\s+form\s*$/i.test(
				t.name || "",
			);
			return { template: t, overlap, endsWithAcceptanceForm };
		})
		.filter(Boolean)
		.sort((a, b) => {
			if (a.endsWithAcceptanceForm !== b.endsWithAcceptanceForm) {
				return a.endsWithAcceptanceForm ? -1 : 1;
			}
			return b.overlap - a.overlap;
		})
		.map((m) => m.template);
};

const VisuallyHiddenInput = styled("input")({
	clip: "rect(0 0 0 0)",
	clipPath: "inset(50%)",
	height: 1,
	overflow: "hidden",
	position: "absolute",
	bottom: 0,
	left: 0,
	whiteSpace: "nowrap",
	width: 1,
});

function ClaimForm() {
	// --- Date helpers to prevent timezone-related day shift ---
	const toLocalDateOnly = (value) => {
		if (!value) return null;
		const d = value instanceof Date ? value : new Date(value);
		if (isNaN(d)) return null;
		return new Date(d.getFullYear(), d.getMonth(), d.getDate());
	};

	const parseServerDate = (value) => {
		if (!value) return null;
		if (value instanceof Date) return toLocalDateOnly(value);
		if (typeof value === "string") {
			// Prefer YYYY-MM-DD parsing to avoid timezone shifts
			const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
			if (m) {
				const y = Number(m[1]);
				const mo = Number(m[2]) - 1;
				const da = Number(m[3]);
				return new Date(y, mo, da);
			}
		}
		return toLocalDateOnly(value);
	};

	const formatDateForApi = (date) => {
		if (!date) return null;
		const d = toLocalDateOnly(date);
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, "0");
		const da = String(d.getDate()).padStart(2, "0");
		return `${y}-${m}-${da}`;
	};
	const { from } = useParams();
	const currentAssignment = useSelector(
		(state) => state.assignment.assignment,
	);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [isFileChanged, setIsFileChanged] = useState(false);
	const [insuredsData, setInsuredsData] = useState([]); // for grid display
	const [insuredsChangeSet, setInsuredsChangeSet] = useState({
		added: [],
		updated: [],
		deleted: [],
	}); // for submit
	const handleInsuredsChange = (changeSet) => {
		setInsuredsChangeSet(changeSet);
		// Clear insured-related validation errors when changes are made
		if (errors.insureds || errors.primaryInsured) {
			setErrors((prev) => ({
				...prev,
				insureds: undefined,
				primaryInsured: undefined,
			}));
		}
	};

	const handleFileChange = (event) => {
		const file = event.target.files[0];
		setIsFileChanged(true);
		if (file) {
			setUploadedFile(file);
		}
	};
	const handleDelete = () => {
		setIsFileChanged(false);
		setUploadedFile(null);
	};

	useEffect(() => {
		if (
			from === "Edit" &&
			currentAssignment?.claimants &&
			currentAssignment?.claimants.length > 0
		) {
			const initial = currentAssignment?.claimants.map((c) => ({
				id: c.id,
				partyId: c.partyId,
				insuredName: c.insuredName,
				insuredContact: c.insuredContactDetails,
				isPrimary: c.isPrimary,
				isNew: false,
			}));
			setInsuredsData(initial);
			setInsuredsChangeSet({ added: [], updated: [], deleted: [] });
		}
	}, [from, currentAssignment?.claimants]);

	const navigate = useNavigate();
	const user = useSelector((state) => state.user.user);

	const [formData, setFormData] = useState(
		from === "Edit"
			? {
					assignmentName: currentAssignment.assignmentName,
					claimNumber: currentAssignment.claimNumber,
					policyNumber: currentAssignment.policyNumber,
					dateOfLoss: parseServerDate(currentAssignment.dateOfLoss),
					assignmentReceived: parseServerDate(
						currentAssignment.assignmentReceived,
					),
					dateOfSurvey: parseServerDate(
						currentAssignment.dateOfSurvey,
					),
					type: currentAssignment.type,
					status: currentAssignment.status,
					policyType: currentAssignment.policyType,
					mode: currentAssignment.mode,
					branch: currentAssignment.branch,
					natureOfLoss: currentAssignment.natureOfLoss,
					insuranceCompany: currentAssignment.insuranceCompany,
					templateId: currentAssignment.templateId
						? currentAssignment.templateId
						: null,
					brokersOrInsurerInvolved:
						currentAssignment.brokersOrInsurerInvolved,
					insurerRemarks: currentAssignment.insurerRemarks,
					assignedAdjuster: currentAssignment.assignedAdjuster,
					assignedManager: currentAssignment.assignedManager,
					docReceivedFull: parseServerDate(
						currentAssignment.docReceivedFull,
					),
					docReceivedPartial: parseServerDate(
						currentAssignment.docReceivedPartial,
					),
					letter1: parseServerDate(currentAssignment.letter1),
					letter2: parseServerDate(currentAssignment.letter2),
					letter3: parseServerDate(currentAssignment.letter3),
					letter4: parseServerDate(currentAssignment.letter4),
					letter5: parseServerDate(currentAssignment.letter5),
					ultimatumLetter: parseServerDate(
						currentAssignment.ultimatumLetter,
					),
					situationOfLoss: currentAssignment.situationOfLoss,
					usageOfPremises: currentAssignment.usageOfPremises,
					natureAndExtentofdamage:
						currentAssignment.natureAndExtentofdamage,
					address: currentAssignment.address,
				}
			: {
					claimNumber: "",
					policyNumber: "",
					address: "",
					dateOfLoss: null,
					assignmentReceived: toLocalDateOnly(new Date()),
					dateOfSurvey: null,
					type: "",
					status: "",
					policyType: "",
					mode: "",
					branch: "",
					natureOfLoss: "",
					insuranceCompany: "",
					templateId: "",
					brokersOrInsurerInvolved: "",
					insurerRemarks: "",
					assignedAdjuster: "",
					assignedManager: "",
					docReceivedFull: null,
					docReceivedPartial: null,
					letter1: null,
					letter2: null,
					letter3: null,
					letter4: null,
					letter5: null,
					ultimatumLetter: null,
					situationOfLoss: "",
					usageOfPremises: "",
					natureAndExtentofdamage: "",
				},
	);
	const [errors, setErrors] = useState({});
	const [adjusters, setAdjusters] = useState([]);
	const [managers, setManagers] = useState([]);
	const [insuranceCompanies, setInsuranceCompanies] = useState([]);
	const [templates, setTemplates] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [templateError, setTemplateError] = useState(null);

	useEffect(() => {
		fetchAdjusters();
		fetchManagers();
		fetchInsuranceCompanies();
		// fetchTemplates(); // Replaced with fetchOpenSignTemplates
		fetchOpenSignTemplatesFromAPI();
	}, []);

	// Helper: pick default template for selected insurance company.
	// Uses token-based matching so company names like "Lonpac Insurance Berhad"
	// match templates named "Lonpac Acceptance Form" (and similar mismatches
	// for STMB / Syarikat Takaful, Liberty, Generali).
	const selectDefaultTemplateId = (companyName, allTemplates) => {
		const matches = findMatchingTemplates(companyName, allTemplates);
		if (matches.length === 0) return "";
		// Pacific has Single / Multiple variants — don't auto-pick, leave to user.
		const hasSingleMultipleSplit = matches.some((t) =>
			/\b(single|multiple)\b/i.test(t.name || ""),
		);
		if (hasSingleMultipleSplit && matches.length > 1) {
			console.warn(
				`Multiple Single/Multiple template variants for "${companyName}" — leaving template selection to user.`,
			);
			return "";
		}
		return matches[0].id;
	};

	// Derived list: templates filtered by selected insurance company.
	const filteredTemplates = formData.insuranceCompany
		? findMatchingTemplates(formData.insuranceCompany, templates)
		: templates;

	const fetchOpenSignTemplatesFromAPI = async () => {
		setLoadingTemplates(true);
		setTemplateError(null);
		try {
			const openSignTemplates = await fetchOpenSignTemplates(); // From api.js
			setTemplates(
				openSignTemplates.map((t) => ({ id: t.id, name: t.name })),
			);
		} catch (error) {
			console.error("Error fetching OpenSign templates:", error);
			setTemplateError("Failed to load signing templates.");
		} finally {
			setLoadingTemplates(false);
		}
	};

	const fetchAdjusters = async () => {
		setLoadingUsers(true);
		try {
			const data = await fetchUsers("Adjuster");
			setAdjusters(data);
		} catch (error) {
			alert("Failed to fetch adjusters. Please try again.");
		} finally {
			setLoadingUsers(false);
		}
	};

	const onClaimClose = () => {
		navigate("/home");
	};

	const fetchManagers = async () => {
		setLoadingUsers(true);
		try {
			const data = await fetchUsers("Manager");

			setManagers(data);
		} catch (error) {
			alert("Failed to fetch managers. Please try again.");
		} finally {
			setLoadingUsers(false);
		}
	};

	const fetchInsuranceCompanies = async () => {
		setLoadingUsers(true);
		try {
			const data = await getInsuranceCompanies();

			setInsuranceCompanies(data);
		} catch (error) {
			alert("Failed to fetch insurance Companies. Please try again.");
		} finally {
			setLoadingUsers(false);
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		if (name === "insuranceCompany") {
			const defaultTemplateId = selectDefaultTemplateId(value, templates);
			setFormData((prev) => ({
				...prev,
				insuranceCompany: value,
				templateId: defaultTemplateId,
			}));
		} else {
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};

	// If templates load after insurance company is set, auto-select default if none selected or invalid
	useEffect(() => {
		if (!formData.insuranceCompany || templates.length === 0) return;
		const currentValid = filteredTemplates.some(
			(t) => t.id === formData.templateId,
		);
		if (!formData.templateId || !currentValid) {
			const defaultTemplateId = selectDefaultTemplateId(
				formData.insuranceCompany,
				templates,
			);
			if (defaultTemplateId) {
				setFormData((prev) => ({
					...prev,
					templateId: defaultTemplateId,
				}));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [templates, formData.insuranceCompany]);

	const handleDateChange = (field, newValue) => {
		setFormData((prevData) => ({
			...prevData,
			[field]: toLocalDateOnly(newValue),
		}));
		setErrors((previousErrors) => ({
			...previousErrors,
			[field]: undefined,
		}));
	};
	const handleSubmit = async (e) => {
		e.preventDefault();

		let validationErrors = {};
		if (!formData.type.trim())
			validationErrors.type = "Assignment type is required.";
		if (!formData.claimNumber.trim())
			validationErrors.claimNumber = "Claim Number is required.";
		if (!formData.policyNumber.trim())
			validationErrors.policyNumber = "Policy number is required.";
		if (!formData.address.trim())
			validationErrors.address = "Insured Address is required.";
		validationErrors = {
			...validationErrors,
			...validateRequiredClaimDates(formData),
		};

		// Validate that at least one insured is added
		const hasInsuredsData =
			insuredsData.length > 0 || insuredsChangeSet.added.length > 0;
		if (!hasInsuredsData) {
			validationErrors.insureds = "At least one insured must be added.";
		}

		// Validate that at least one primary insured exists
		const hasPrimaryInsured =
			insuredsData.some((insured) => insured.isPrimary) ||
			insuredsChangeSet.added.some((insured) => insured.isPrimary);
		if (!hasPrimaryInsured && hasInsuredsData) {
			validationErrors.primaryInsured =
				"At least one insured must be marked as primary.";
		}

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		setIsSubmitting(true);
		try {
			if (formData.assignedAdjuster === "") {
				formData.status = "NEW";
			} else {
				formData.status = "Assigned";
			}

			if (from === "New") {
				// Serialize dates as YYYY-MM-DD to avoid timezone shifts
				const assignmentPayload = {
					...formData,
					dateOfLoss: formatDateForApi(formData.dateOfLoss),
					assignmentReceived: formatDateForApi(
						formData.assignmentReceived,
					),
					dateOfSurvey: formatDateForApi(formData.dateOfSurvey),
					docReceivedFull: formatDateForApi(formData.docReceivedFull),
					docReceivedPartial: formatDateForApi(
						formData.docReceivedPartial,
					),
					letter1: formatDateForApi(formData.letter1),
					letter2: formatDateForApi(formData.letter2),
					letter3: formatDateForApi(formData.letter3),
					letter4: formatDateForApi(formData.letter4),
					letter5: formatDateForApi(formData.letter5),
					ultimatumLetter: formatDateForApi(formData.ultimatumLetter),
				};
				const now = new Date().toISOString();
				const result = await createAssignment(assignmentPayload);
				const resAssignment = await result.message;
				const commonFields = {
					assignmentName: resAssignment,
					createdBy: user ? user.userName : "System",
					createdDatetime: now,
					lastUpdatedDatetime: now,
					lastUpdatedBy: user ? user.userName : "System",
				};
				const claimantPayload = insuredsChangeSet.added.map((item) => ({
					id: item.id,
					partyId: generateBase64UUID(),
					assignmentName: commonFields.assignmentName,
					insuredName: item.insuredName,
					insuredContactDetails: item.insuredContact,
					isPrimary: item.isPrimary,
					createdBy: commonFields.createdBy,
					createdDatetime: commonFields.createdDatetime,
					lastUpdatedDatetime: commonFields.lastUpdatedDatetime,
					lastUpdatedBy: commonFields.lastUpdatedBy,
				}));

				await createClaimant(claimantPayload);

				const FileToUpload = new FormData();
				if (isFileChanged) {
					const reader = new FileReader();
					reader.onload = (e) => {
						const fileContent = e.target.result;
						const blob = new Blob([fileContent], {
							type: uploadedFile.type,
						});
						FileToUpload.append(
							"dvform",
							blob,
							"Form_of_Acceptance(P).pdf",
						);
						FileToUpload.append("assignmentName", resAssignment);
						FileToUpload.append(
							"AttachmentDetails",
							JSON.stringify([
								{
									FileName: "Form_of_Acceptance(P).pdf",
								},
							]),
						);
						uploadAttachment(FileToUpload);
					};
					reader.readAsArrayBuffer(uploadedFile); // Read the file as an ArrayBuffer
				}

				alert("Assignment created successfully!");
			} else {
				formData.status = currentAssignment.status;

				// Serialize dates as YYYY-MM-DD to avoid timezone shifts
				const assignmentPayload = {
					...formData,
					dateOfLoss: formatDateForApi(formData.dateOfLoss),
					assignmentReceived: formatDateForApi(
						formData.assignmentReceived,
					),
					dateOfSurvey: formatDateForApi(formData.dateOfSurvey),
					docReceivedFull: formatDateForApi(formData.docReceivedFull),
					docReceivedPartial: formatDateForApi(
						formData.docReceivedPartial,
					),
					letter1: formatDateForApi(formData.letter1),
					letter2: formatDateForApi(formData.letter2),
					letter3: formatDateForApi(formData.letter3),
					letter4: formatDateForApi(formData.letter4),
					letter5: formatDateForApi(formData.letter5),
					ultimatumLetter: formatDateForApi(formData.ultimatumLetter),
				};

				await updateAssignment(assignmentPayload, user);

				// Handle insureds update
				if (insuredsChangeSet) {
					if (insuredsChangeSet.added?.length) {
						const now = new Date().toISOString();
						const commonFields = {
							assignmentName: currentAssignment.assignmentName,
							createdBy: user ? user.userName : "system",
							createdDatetime: now,
							lastUpdatedDatetime: now,
							lastUpdatedBy: user ? user.userName : "system",
						};
						const claimantPayload = insuredsChangeSet.added.map(
							(item) => ({
								id: item.id,
								partyId: generateBase64UUID(),
								assignmentName: commonFields.assignmentName,
								insuredName: item.insuredName,
								insuredContactDetails: item.insuredContact,
								isPrimary: item.isPrimary,
								createdBy: commonFields.createdBy,
								createdDatetime: commonFields.createdDatetime,
								lastUpdatedDatetime:
									commonFields.lastUpdatedDatetime,
								lastUpdatedBy: commonFields.lastUpdatedBy,
							}),
						);
						await createClaimant(claimantPayload);
					}
					if (insuredsChangeSet.updated?.length) {
						const now = new Date().toISOString();
						const updatePayload = insuredsChangeSet.updated.map(
							(item) => ({
								id: item.id,
								partyId:
									item.partyId || generateBase64UUID(),
								assignmentName:
									currentAssignment.assignmentName,
								insuredName: item.insuredName,
								insuredContactDetails: item.insuredContact,
								isPrimary: item.isPrimary,
								createdBy: user ? user.userName : "System",
								createdDatetime: now,
								lastUpdatedDatetime: now,
								lastUpdatedBy: user ? user.userName : "System",
							}),
						);

						await updateClaimant(updatePayload);
					}
					if (insuredsChangeSet.deleted?.length) {
						const deleteIds = insuredsChangeSet.deleted.map(
							(item) => item.id,
						);
						await deleteClaimant(deleteIds);
					}
				}

				if (uploadedFile && isFileChanged) {
					const reader = new FileReader();
					reader.onload = (e) => {
						const fileContent = e.target.result;
						const blob = new Blob([fileContent], {
							type: uploadedFile.type,
						});
						const formDataToUpload = new FormData();

						formDataToUpload.append(
							"dvform",
							blob,
							"Form_of_Acceptance(P).pdf",
						);
						formDataToUpload.append(
							"assignmentName",
							currentAssignment.assignmentName,
						);
						formDataToUpload.append(
							"AttachmentDetails",
							JSON.stringify([
								{
									FileName: "Form_of_Acceptance(P).pdf",
								},
							]),
						);
						uploadAttachment(formDataToUpload);
					};
					reader.readAsArrayBuffer(uploadedFile); // Read the file as an ArrayBuffer
				}
				alert("Assignment Details Updated!");
			}
			setFormData({
				claimNumber: "",
				policyNumber: "",
				address: "",
				dateOfLoss: null,
				assignmentReceived: new Date(),
				dateOfSurvey: null,
				type: "",
				status: "",
				policyType: "",
				mode: "",
				branch: "",
				natureOfLoss: "",
				insuranceCompany: "",
				templateId: "",
				brokersOrInsurerInvolved: "",
				insurerRemarks: "",
				assignedAdjuster: "",
				assignedManager: "",
				docReceivedFull: null,
				docReceivedPartial: null,
				letter1: null,
				letter2: null,
				letter3: null,
				letter4: null,
				letter5: null,
				ultimatumLetter: null,
				situationOfLoss: "",
				usageOfPremises: "",
				natureAndExtentofdamage: "",
			});
			navigate("/home");
		} catch (error) {
			alert("Failed to create assignment. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFileUpload = async (event) => {
		const file = event.target.files[0];
		if (file) {
			if (file.name.endsWith(".csv")) {
				const formFile = new FormData();
				formFile.append("file", file);
				await uploadBulkAssignments(formFile);
				alert(`File uploaded for bulk creation`);
			} else {
				alert("Invalid file type! Please upload an Excel file.");
			}
		}
	};

	if (!loadingUsers) {
		return (
			<Box
				sx={{
					width: "100%",
					margin: "auto",
				}}
			>
				<AppBar position="sticky" color="primary">
					<Toolbar>
						<Typography variant="h6" sx={{ flexGrow: 1 }}>
							Claim Form
						</Typography>
						<IconButton
							edge="end"
							color="inherit"
							onClick={onClaimClose}
						>
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>

				<Container
					component={Paper}
					elevation={4}
					sx={{ padding: { xs: 1.5, sm: 2, md: 4 }, mt: 2 }}
				>
					{from === "New" && (
						<Box sx={{ margin: { xs: "10px", md: "20px" } }}>
							<Button
								variant="contained"
								component="label"
								sx={{ padding: "10px 20px" }}
							>
								Upload Bulk Assignments
								<input
									type="file"
									hidden
									accept=".csv" // Restrict file types
									onChange={handleFileUpload}
								/>
							</Button>
						</Box>
					)}
					<Box component="form" onSubmit={handleSubmit}>
						{/* Claim Information */}
						<Typography variant="h6" gutterBottom>
							Claim Information
						</Typography>
						<Divider sx={{ mb: 3 }} />
						<Grid2 container spacing={2}>
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<TextField
									required
									fullWidth
									label="Claim Number"
									name="claimNumber"
									value={formData.claimNumber}
									onChange={handleInputChange}
									error={!!errors.claimNumber}
									helperText={errors.claimNumber}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<TextField
									required
									fullWidth
									label="Policy Number"
									name="policyNumber"
									value={formData.policyNumber}
									onChange={handleInputChange}
									error={!!errors.policyNumber}
									helperText={errors.policyNumber}
								/>
							</Grid2>
							<Grid2 item size={12}>
								<TextField
									required
									fullWidth
									label="Insured Address"
									name="address"
									value={formData.address}
									onChange={handleInputChange}
									error={!!errors.address}
									helperText={errors.address}
								/>
							</Grid2>{" "}
							<Grid2 item size={12}>
								<Box sx={{ width: "100%" }}>
									<InsuredCrudGrid
										value={insuredsData}
										onChange={handleInsuredsChange}
									/>
									{errors.insureds && (
										<Typography
											color="error"
											variant="caption"
											sx={{ mt: 1, display: "block" }}
										>
											{errors.insureds}
										</Typography>
									)}
									{errors.primaryInsured && (
										<Typography
											color="error"
											variant="caption"
											sx={{ mt: 1, display: "block" }}
										>
											{errors.primaryInsured}
										</Typography>
									)}
									<Typography
										variant="caption"
										sx={{
											mt: 1,
											display: "block",
											color: "text.secondary",
										}}
									>
										Phone numbers should be 9-11 digits.
										Numbers starting with 0 will
										automatically be prefixed with +6.
									</Typography>
								</Box>
							</Grid2>
						</Grid2>

						{/* Date Information */}
						<Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
							Date Information
						</Typography>
						<Divider sx={{ mb: 3 }} />
						<Grid2 container spacing={1}>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Date of Loss"
										value={formData.dateOfLoss}
										onChange={(newValue) =>
											handleDateChange(
												"dateOfLoss",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: {
												fullWidth: true,
												required: true,
												error: Boolean(errors.dateOfLoss),
												helperText: errors.dateOfLoss,
											},
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Assignment Received"
										value={formData.assignmentReceived}
										onChange={(newValue) =>
											handleDateChange(
												"assignmentReceived",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Date of Survey"
										value={formData.dateOfSurvey}
										onChange={(newValue) =>
											handleDateChange(
												"dateOfSurvey",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: {
												fullWidth: true,
												required: true,
												error: Boolean(errors.dateOfSurvey),
												helperText: errors.dateOfSurvey,
											},
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
						</Grid2>

						{/* Assignment Details */}
						<Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
							Assignment Details
						</Typography>
						<Divider sx={{ mb: 3 }} />
						<Grid2 container spacing={2}>
							<Grid2 item size={{ xs: 12, sm: 6, md: 3 }}>
								<TextField
									select
									fullWidth
									label="Assignment Type"
									name="type"
									value={formData.type}
									onChange={handleInputChange}
									required
								>
									{assignmentTypes.map((type, index) => (
										<MenuItem key={index} value={type}>
											{type}
										</MenuItem>
									))}
								</TextField>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 6, md: 3 }}>
								<TextField
									select
									fullWidth
									label="Nature of Loss"
									name="natureOfLoss"
									value={formData.natureOfLoss}
									onChange={handleInputChange}
									required
									helperText="FL - Flood, WS - Windstorm, FLWS - Flood & Windstorm"
								>
									{natureOfLosses.map(
										(natureOfLoss, index) => (
											<MenuItem
												key={index}
												value={natureOfLoss}
											>
												{natureOfLoss}
											</MenuItem>
										),
									)}
								</TextField>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 6, md: 3 }}>
								<TextField
									select
									fullWidth
									label="Branch"
									name="branch"
									value={formData.branch}
									onChange={handleInputChange}
									required
								>
									{branches.map((branch, index) => (
										<MenuItem key={index} value={branch}>
											{branch}
										</MenuItem>
									))}
								</TextField>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 6, md: 3 }}>
								<Autocomplete
									freeSolo
									options={policyTypes}
									value={formData.policyType || ""}
									onInputChange={(event, newInputValue) => {
										setFormData((prev) => ({
											...prev,
											policyType: newInputValue || "",
										}));
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											fullWidth
											label="Policy Type"
											name="policyType"
											required
										/>
									)}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<TextField
									select
									fullWidth
									label="Insurance Company"
									name="insuranceCompany"
									value={formData.insuranceCompany}
									onChange={handleInputChange}
									required
								>
									{insuranceCompanies.map((company) => (
										<MenuItem
											key={company.id}
											value={company.name}
										>
											{company.name}
										</MenuItem>
									))}
								</TextField>
							</Grid2>{" "}
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<Box sx={{ position: "relative" }}>
									<TextField
										select
										fullWidth
										label="Acceptance Form Template"
										name="templateId"
										value={formData.templateId || ""}
										onChange={handleInputChange}
										disabled={loadingTemplates}
										helperText={templateError}
										error={!!templateError}
										slotProps={{
											InputProps: {
												endAdornment: (
													<IconButton
														size="small"
														onClick={() =>
															fetchOpenSignTemplatesFromAPI()
														}
														disabled={
															loadingTemplates
														}
														sx={{
															visibility:
																templateError
																	? "visible"
																	: "hidden",
														}}
													>
														<RefreshIcon fontSize="small" />
													</IconButton>
												),
											},
										}}
									>
										{loadingTemplates ? (
											<MenuItem disabled>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
													}}
												>
													<CircularProgress
														size={20}
														sx={{ mr: 1 }}
													/>
													Loading templates...
												</Box>
											</MenuItem>
										) : filteredTemplates.length > 0 ? (
											[
												<MenuItem key="none" value="">
													<em>None (No DV Form)</em>
												</MenuItem>,
												...filteredTemplates.map(
													(template) => (
														<MenuItem
															key={template.id}
															value={template.id}
														>
															{template.name}{" "}
															{template.hasWitness
																? "(Witness Required)"
																: template.claimantCount >
																	  1
																	? "(Multiple Claimants)"
																	: ""}
														</MenuItem>
													),
												),
											]
										) : (
											[
												<MenuItem key="none" value="">
													<em>None (No DV Form)</em>
												</MenuItem>,
												<MenuItem
													key="no-templates"
													disabled
												>
													No templates available
												</MenuItem>,
											]
										)}
									</TextField>
									{loadingTemplates && (
										<CircularProgress
											size={24}
											sx={{
												position: "absolute",
												top: "50%",
												right: "14px",
												marginTop: "-12px",
												marginRight: "36px",
											}}
										/>
									)}
								</Box>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="Brokers/Insurers Involved"
									name="brokersOrInsurerInvolved"
									value={formData.brokersOrInsurerInvolved}
									onChange={handleInputChange}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="Situation of loss"
									name="situationOfLoss"
									value={formData.situationOfLoss}
									onChange={handleInputChange}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4 }}>
								<TextField
									fullWidth
									required
									label="Usage of Premises"
									name="usageOfPremises"
									value={formData.usageOfPremises}
									onChange={handleInputChange}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<TextField
									fullWidth
									multiline
									rows={2}
									label="Nature and Extent of Damage"
									name="natureAndExtentofdamage"
									value={formData.natureAndExtentofdamage}
									onChange={handleInputChange}
								/>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 6 }}>
								<TextField
									fullWidth
									multiline
									rows={2}
									label="Insurer's Remarks"
									name="insurerRemarks"
									value={formData.insurerRemarks}
									onChange={handleInputChange}
								/>
							</Grid2>
							<Grid2 item size={12}>
								<Stack
									direction="row"
									spacing={1}
									sx={{ flexWrap: "wrap" }}
								>
									<Button
										component="label"
										role={undefined}
										variant="contained"
										tabIndex={-1}
										startIcon={<CloudUploadIcon />}
										disabled={!!uploadedFile}
									>
										Upload DV Form
										<VisuallyHiddenInput
											type="file"
											accept=".pdf" // Allow only PDF and DOC files
											onChange={handleFileChange}
											single
										/>
									</Button>

									{uploadedFile &&
										(from === "Edit" ? (
											<Chip
												label={uploadedFile.name}
												variant="outlined"
												color="success"
											/>
										) : (
											<Chip
												label={uploadedFile.name}
												variant="outlined"
												color="success"
												onDelete={handleDelete}
											/>
										))}
								</Stack>
							</Grid2>
						</Grid2>
						{/* Reminders Details */}
						<Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
							Reminders/Letters
						</Typography>
						<Divider sx={{ mb: 3 }} />
						<Grid2 container spacing={1}>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Doc. Received Partial"
										value={formData.docReceivedPartial}
										onChange={(newValue) =>
											handleDateChange(
												"docReceivedPartial",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Doc. Received Full"
										value={formData.docReceivedFull}
										onChange={(newValue) =>
											handleDateChange(
												"docReceivedFull",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Letter 1"
										value={formData.letter1}
										onChange={(newValue) =>
											handleDateChange(
												"Letter1",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Letter 2"
										value={formData.letter2}
										onChange={(newValue) =>
											handleDateChange(
												"Letter2",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Letter 3"
										value={formData.letter3}
										onChange={(newValue) =>
											handleDateChange(
												"Letter3",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 4, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Letter 4"
										value={formData.letter4}
										onChange={(newValue) =>
											handleDateChange(
												"Letter4",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 6, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Letter 5"
										value={formData.letter5}
										onChange={(newValue) =>
											handleDateChange(
												"Letter5",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
							<Grid2 item size={{ xs: 6, sm: 6, md: 3 }}>
								<LocalizationProvider
									dateAdapter={AdapterDateFns}
								>
									<DatePicker
										label="Ultimatum Letter"
										value={formData.ultimatumLetter}
										onChange={(newValue) =>
											handleDateChange(
												"ultimatumLetter",
												newValue,
											)
										}
										format="dd/MM/yyyy"
										slotProps={{
											textField: { fullWidth: true },
											field: { clearable: true },
										}}
									/>
								</LocalizationProvider>
							</Grid2>
						</Grid2>
						{/* Release Details */}
						<Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
							Release
						</Typography>
						<Divider sx={{ mb: 3 }} />
						<Grid2 container spacing={1}>
							<Grid2 item size={{ xs: 12, sm: 4, md: 2 }}>
								<TextField
									select
									fullWidth
									label="Assignment mode"
									name="mode"
									value={formData.mode}
									onChange={handleInputChange}
									required
									helperText="R - Remote, P - Physical"
								>
									{assignmentModes.map(
										(assignmentMode, index) => (
											<MenuItem
												key={index}
												value={assignmentMode}
											>
												{assignmentMode}
											</MenuItem>
										),
									)}
								</TextField>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4, md: 5 }}>
								<TextField
									select
									required
									fullWidth
									label="Assign To Adjuster"
									name="assignedAdjuster"
									value={formData.assignedAdjuster}
									onChange={handleInputChange}
									slotProps={{
										textField: { fullWidth: true },
										field: { clearable: true },
									}}
								>
									{adjusters.map((adjuster) => (
										<MenuItem
											key={adjuster.userName}
											value={adjuster.userName}
										>
											{adjuster.name}
										</MenuItem>
									))}
								</TextField>
							</Grid2>
							<Grid2 item size={{ xs: 12, sm: 4, md: 5 }}>
								<TextField
									select
									fullWidth
									label="Assign To Manager"
									name="assignedManager"
									value={formData.assignedManager}
									onChange={handleInputChange}
									required
								>
									{managers.map((manager) => (
										<MenuItem
											key={manager.userName}
											value={manager.userName}
										>
											{manager.name}
										</MenuItem>
									))}
								</TextField>
							</Grid2>
						</Grid2>
						{/* Submit Button */}
						<Box textAlign="center" sx={{ mt: 4 }}>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								fullWidth
								sx={{ mt: 2 }}
								disabled={isSubmitting}
								startIcon={
									isSubmitting && (
										<CircularProgress size={20} />
									)
								}
							>
								{isSubmitting
									? "Submitting..."
									: from === "New"
										? "Create"
										: "Update"}
							</Button>
						</Box>
					</Box>
				</Container>
			</Box>
		);
	} else {
		return (
			<Box sx={{ display: "flex" }}>
				<CircularProgress />
			</Box>
		);
	}
}

export default ClaimForm;
